import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import Typography from '@tiptap/extension-typography';
import { Extension, mergeAttributes } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { all, createLowlight } from 'lowlight';
import { EditorNodeType } from '../hooks/types';
import i18n from '../i18n/i18n';
import { createSuggestion } from './suggestion';
import { OPEN_SPACE_AI } from '../const';
import { MathExtension } from './plugin/math-extension';
const lowlight = createLowlight(all);

export const HeadingStyles = Extension.create({
  name: 'headingStyles',

  addGlobalAttributes() {
    return [
      {
        types: [EditorNodeType.HEADING],
        attributes: {
          level: {
            default: 1,
            renderHTML: (attributes) => {
              const styles = {
                // 1: 'mt-6 text-h1',
                // 2: 'mt-4 text-h2',
                1: 'mt-6 text-h3', //设计说字号太大了，全部调成最大h3
                2: 'mt-4 text-h4',
                3: 'mt-2 text-h3',
                4: 'mt-px text-h4',
                5: 'mt-px text-h5',
              }[attributes.level as number];

              return {
                class: styles,
              };
            },
          },
        },
      },
    ];
  },
});

export const CodeBlockStyles = CodeBlockLowlight.configure({
  lowlight,
  languageClassPrefix: 'language-',
  HTMLAttributes: {
    class: 'bg-black1 text-white rounded-lg p-2',
  },
});
export const BaseExtension = [
  MathExtension,
  CodeBlockStyles,
  Typography,
  StarterKit.configure({
    codeBlock: false,
    code: false,
    paragraph: {
      HTMLAttributes: {
        class: 'py-2',
      },
    },
    bulletList: {
      HTMLAttributes: {
        class: 'list-disc pl-6',
      },
    },
    orderedList: {
      HTMLAttributes: {
        class: 'list-decimal pl-8',
      },
    },
    blockquote: {
      HTMLAttributes: {
        class: 'pl-2.5 border-l-4 border-gray-200',
      },
    },
    horizontalRule: {
      HTMLAttributes: {
        class: 'my-2',
      },
    },
  }),
  HeadingStyles,
  Link.configure({
    openOnClick: true,
    HTMLAttributes: {
      class: 'text-active_color underline underline-offset-4 mx-0.5',
    },
  }),
  TaskList,
  TaskItem.configure({
    nested: true,
  }),
  Table.configure({
    resizable: false,
    HTMLAttributes: {
      class: 'border-collapse w-full',
    },
  }),
  TableRow.configure({
    HTMLAttributes: {
      class: 'border-b',
    },
  }),
  TableHeader.configure({
    HTMLAttributes: {
      class: 'border p-2',
    },
  }),
  TableCell.configure({
    HTMLAttributes: {
      class: 'border p-2',
      align: 'center',
    },
  }),
  Image.extend({
    addAttributes() {
      const attrs = this.parent?.() || {};

      return {
        ...attrs,
        width: {
          default: null,
          parseHTML: (el) => el.getAttribute('width'),
          renderHTML: (attrs) => (attrs.width ? { width: attrs.width } : {}),
        },
      };
    },
  }).configure({
    HTMLAttributes: {
      class: 'rounded-lg inline-block',
    },
    inline: true,
  }),
];

interface TiptapConfig {
  spaceId: string;
}

export const createTiptapExtensions = ({ spaceId }: TiptapConfig) =>
  [
    StarterKit.configure({
      codeBlock: false,
      heading: false,
      bold: false,
      italic: false,
      strike: false,
      code: false,
    }),
    CodeBlockStyles,
    Typography,

    OPEN_SPACE_AI &&
      Mention.configure({
        HTMLAttributes: {
          class: 'bg-active_color_10 text-active_color rounded px-1 py-px',
          target: '_blank',
        },
        deleteTriggerWithBackspace: true,
        renderHTML({ options, node }) {
          const { id, label } = node.attrs;
          return [
            'a',
            mergeAttributes({ href: `/${id}` }, options.HTMLAttributes),
            `${options.suggestion.char}${label}`,
          ];
        },
        suggestion: {
          ...createSuggestion({ spaceId }),
          command: ({ editor, range, props }) => {
            editor
              .chain()
              .focus()
              .deleteRange(range)
              .insertContent({
                type: 'mention',
                attrs: props,
              })
              .insertContent(' ')
              .run();
          },
        },
      }),

    Placeholder.configure({
      placeholder: i18n.t('chatInputPlaceholder') as string,
      emptyEditorClass:
        'before:content-[attr(data-placeholder)] before:text-grey3 before:float-left before:h-0 before:pointer-events-none text-t1',
    }),
  ] as any[];
